import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	createDirecionada,
	deleteDirecionada,
	getDirecionadaAllList,
	getDirecionadaById,
	getDirecionadaList,
	getDirecionadaMeList,
	updateDirecionada,
} from '@/api/services/direcionada/direcionada'
import { useProfileStore } from '@/store'
import type {
	DirecionadaAllListOutput,
	DirecionadaAllListParams,
	DirecionadaListOutput,
	DirecionadaListParams,
	DirecionadaMeListOutput,
	DirecionadaMeListParams,
	DirecionadaOutput,
	DirecionadaUpdateInput,
} from '@/types/direcionada'

const QUERY_KEY = ['direcionada']
const ME_QUERY_KEY = ['direcionada', 'me-list']
const PENDENCIES_QUERY_KEY = ['pendencies', 'me']
export function useCreateDirecionada() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: createDirecionada,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: PENDENCIES_QUERY_KEY })
		},
	})
}

export function useDirecionadaMeList(params?: DirecionadaMeListParams) {
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const scopedParams: DirecionadaMeListParams = {
		...params,
		scope: params?.scope ?? 'branch',
		activeBranchId: params?.activeBranchId ?? (defaultBranchId || undefined),
		activeCrId: params?.activeCrId ?? (defaultContractId || undefined),
	}

	return useQuery<DirecionadaMeListOutput, Error>({
		queryKey: [...ME_QUERY_KEY, scopedParams],
		queryFn: () => getDirecionadaMeList(scopedParams),
		staleTime: 10 * 60 * 1000,
	})
}

export function useDirecionadaAllList(params?: DirecionadaAllListParams) {
	const { defaultBranchId, defaultContractId } = useProfileStore()
	const scopedParams: DirecionadaAllListParams = {
		...params,
		scope: params?.scope ?? 'branch',
		activeBranchId: params?.activeBranchId ?? (defaultBranchId || undefined),
		activeCrId: params?.activeCrId ?? (defaultContractId || undefined),
	}

	return useQuery<DirecionadaAllListOutput, Error>({
		queryKey: [...ME_QUERY_KEY, scopedParams],
		queryFn: () => getDirecionadaAllList(scopedParams),
		staleTime: 10 * 60 * 1000,
	})
}

export function useDirecionadaList(params?: DirecionadaListParams) {
	return useQuery<DirecionadaListOutput, Error>({
		queryKey: [...QUERY_KEY, 'list', params],
		queryFn: () => getDirecionadaList(params),
		staleTime: 10 * 60 * 1000, // 10 minutos
	})
}

export function useDirecionadaById(id?: string) {
	return useQuery<DirecionadaOutput, Error>({
		queryKey: [...QUERY_KEY, id],
		queryFn: () => {
			if (!id) {
				throw new Error('Direcionada ID is required')
			}
			return getDirecionadaById(id)
		},
		enabled: Boolean(id),
		staleTime: 10 * 60 * 1000,
	})
}

export function useUpdateDirecionada() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: DirecionadaUpdateInput }) =>
			updateDirecionada(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: PENDENCIES_QUERY_KEY })
		},
	})
}

export function useDeleteDirecionada() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: deleteDirecionada,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
			queryClient.invalidateQueries({ queryKey: PENDENCIES_QUERY_KEY })
		},
	})
}
